import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resourceApi, type ResourceType } from '../lib/api';
import { POI_EMOJI } from './PoiPanel';
import { useApp } from '../store';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../i18n/format';

const ORDER: ResourceType[] = ['science', 'military', 'economy', 'spirituality', 'culture'];

const AMOUNT_COLOR: Record<ResourceType, string> = {
  science: '#4ade80',
  military: '#f87171',
  economy: '#60a5fa',
  spirituality: '#c084fc',
  culture: '#f59e0b',
};

/**
 * Deux plus grosses catégories d’ordre de grandeur.
 * 2 715 322 → 2M715 · 1 500 → 1K500 · 999 → 999 · 2 000 000 000 → 2B
 */
export function formatCompactResource(value: number): string {
  const n = Math.floor(Math.max(0, Number.isFinite(value) ? value : 0));
  if (n < 1000) return String(n);

  const B = 1_000_000_000;
  const M = 1_000_000;
  const K = 1_000;

  if (n >= B) {
    const major = Math.floor(n / B);
    const minor = Math.floor((n % B) / M);
    return minor > 0 ? `${major}B${minor}` : `${major}B`;
  }
  if (n >= M) {
    const major = Math.floor(n / M);
    const minor = Math.floor((n % M) / K);
    return minor > 0 ? `${major}M${minor}` : `${major}M`;
  }
  const major = Math.floor(n / K);
  const minor = n % K;
  return minor > 0 ? `${major}K${minor}` : `${major}K`;
}

function formatCompactSigned(value: number): string {
  const abs = Math.abs(Math.round(value));
  const body = formatCompactResource(abs);
  if (value > 0) return `+${body}`;
  if (value < 0) return `-${body}`;
  return body;
}

export function ResourceHud() {
  const { t } = useTranslation(['panels', 'common']);
  const { user, setPanel, interfaceHidden } = useApp();
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);
  const [paused, setPaused] = useState(false);

  const summary = useQuery({
    queryKey: ['resource-summary'],
    queryFn: () => resourceApi.summary(),
    enabled: !!user,
    staleTime: 45_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  // Stocks serveur uniquement — pas d'extrapolation entre les crédits horaires.
  const amounts = useMemo(() => {
    const balances = summary.data?.balances;
    const rates = summary.data?.hourlyRates;
    if (!balances) return null;
    const out: Record<ResourceType, { amount: number; rate: number }> = {
      science: { amount: 0, rate: 0 },
      military: { amount: 0, rate: 0 },
      economy: { amount: 0, rate: 0 },
      spirituality: { amount: 0, rate: 0 },
      culture: { amount: 0, rate: 0 },
    };
    for (const t of ORDER) {
      out[t] = {
        amount: Number(balances[t]?.balance ?? 0),
        rate: Number(rates?.[t] ?? 0),
      };
    }
    return out;
  }, [summary.data?.balances, summary.data?.hourlyRates]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const measure = () => {
      const overflow = Math.max(0, track.scrollWidth - viewport.clientWidth);
      setOverflowPx(overflow);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(track);
    return () => ro.disconnect();
  }, [amounts]);

  if (!user || interfaceHidden) return null;
  if (!amounts && summary.isLoading) {
    return (
      <div className="pointer-events-none flex items-center gap-2 rounded-full border border-white/5 bg-[#0b1219]/25 px-2.5 py-1 opacity-40 sm:px-3">
        <span className="text-[10px] text-white/50">…</span>
      </div>
    );
  }
  if (!amounts) return null;

  const scrolling = overflowPx > 2;
  // ~28 px/s, min 6 s aller-retour pour rester lisible
  const durationSec = scrolling ? Math.max(6, (overflowPx * 2) / 28) : 0;

  return (
    <div
      ref={viewportRef}
      className="resource-hud pointer-events-auto max-w-full overflow-hidden rounded-full border border-white/[0.08] bg-gradient-to-r from-[#0b1219]/45 via-[#0b1219]/30 to-transparent px-1.5 py-1 shadow-[0_0_24px_rgba(0,0,0,0.15)] backdrop-blur-md sm:px-2.5 sm:py-1.5"
      title={t('common:nav.resources')}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
    >
      <div
        ref={trackRef}
        className={`flex w-max items-center gap-1 sm:gap-2 ${scrolling ? 'resource-hud-marquee' : ''}`}
        style={
          scrolling
            ? ({
                '--resource-hud-shift': `-${overflowPx}px`,
                '--resource-hud-duration': `${durationSec}s`,
                animationPlayState: paused ? 'paused' : 'running',
              } as CSSProperties)
            : undefined
        }
      >
        {ORDER.map((type) => {
          const { amount, rate } = amounts[type];
          const label = formatCompactResource(amount);
          const prod = Math.round(rate);
          const prodLabel = prod !== 0 ? `(${formatCompactSigned(prod)})` : null;
          return (
            <button
              key={type}
              type="button"
              onClick={() => setPanel('resources')}
              className="group flex shrink-0 items-baseline gap-0.5 rounded-md px-0.5 py-0.5 transition hover:bg-white/[0.04] sm:gap-1 sm:px-1"
              title={t('panels:resourceHud.openResources', { amount: formatNumber(Math.floor(amount)), prod: prod !== 0 ? ` · ${prod > 0 ? '+' : ''}${prod}/h` : '' })}
            >
              <span className="text-[10px] leading-none opacity-55 transition group-hover:opacity-90 sm:text-[13px]" aria-hidden>
                {POI_EMOJI[type]}
              </span>
              <span
                className="font-[family-name:var(--font-display)] text-[10px] font-semibold tabular-nums tracking-wide opacity-75 transition group-hover:opacity-100 sm:text-[12px]"
                style={{ color: AMOUNT_COLOR[type], textShadow: `0 0 12px ${AMOUNT_COLOR[type]}33` }}
              >
                {label}
                {prodLabel && (
                  <span className="ml-0.5 font-medium opacity-70 sm:ml-1">{prodLabel}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
